/* ============================================================
   ec-motion.js
   Motion-Logik der Startseite. Läuft nur im Index-Template.
   Erwartet: gsap, ScrollTrigger, Lenis (lokale Theme-Assets).

   Grundsätze
   - Gepinnte Strecken werden über CSS `position: sticky` gelöst,
     nicht über ScrollTrigger-Pinning. Das ist auf Mobile deutlich
     stabiler (Adressleisten-Resize erzeugt keinen Sprung).
   - Jede Sektion trägt `data-ec-section="<name>"`; initialisiert
     wird ausschliesslich, was im DOM vorhanden ist.
   - prefers-reduced-motion: keine Scrubs, keine Parallax, nur Fades.
   ============================================================ */

(function () {
  'use strict';

  var root = document.documentElement;
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Fallback: Bibliotheken fehlen → Inhalte einfach zeigen. */
  if (!gsap || !ScrollTrigger) {
    root.classList.remove('ec-motion-on');
    root.classList.add('ec-motion-off');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* Auf Mobilgeräten ändert das Ein- und Ausfahren der Adressleiste die
     Fensterhöhe. Ohne diese Einstellung vermisst ScrollTrigger die Seite
     mitten im Scrollen neu — das ist der Sprung, bei dem gehaltene
     Sektionen an der falschen Stelle stehen bleiben. */
  ScrollTrigger.config({ ignoreMobileResize: true });

  var touch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /* Auf schmalen Displays kein Hero-Video laden. Läuft bewusst sofort,
     also bevor das Video-Element des Themes initialisiert wird. */
  if (window.innerWidth <= 900) {
    var novideo = document.querySelectorAll('[data-ec-hero-mobile-novideo] video-background-element');
    for (var n = 0; n < novideo.length; n++) { novideo[n].parentNode.removeChild(novideo[n]); }
  }

  var EASE_EXPO = 'expo.out';
  var DUR_LINE = 1.0;
  var DUR_IMG = 1.2;
  var STAGGER_LINE = 0.12;
  var STAGGER_ITEM = 0.15;
  var START = 'top 78%';

  /* Mit dem Finger wird ruckartig geworfen, danach läuft der Schwung aus.
     Ein Nachlauf von 0.8 s wirkt dabei, als würde die Sektion der Bewegung
     hinterherhinken und erst spät zur Ruhe kommen. Deshalb auf Touch kürzer. */
  var SCRUB = touch ? 0.3 : 0.8;

  /* ==========================================================
     Smooth Scrolling (Lenis)
     ========================================================== */

  var lenis = null;

  function initLenis() {
    if (reduced || !window.Lenis) return;

    lenis = new window.Lenis({
      duration: 1.05,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      syncTouch: false,          /* Touch bleibt nativ – nie blockieren */
      autoRaf: false,
      prevent: function (node) {
        return node.nodeType === 1 && (
          node.hasAttribute('data-lenis-prevent') ||
          node.classList.contains('site-nav') ||
          node.classList.contains('modal') ||
          node.classList.contains('site-sidebar')
        );
      }
    });

    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    /* Wenn das Theme ein Overlay öffnet (Warenkorb, Menü, Popup),
       darf die Seite dahinter nicht weiterscrollen. Das Theme setzt
       dafür `html.kill-overflow` bzw. `body.modal-opened`. */
    var lock = new MutationObserver(function () {
      if (root.classList.contains('kill-overflow')) { lenis.stop(); } else { lenis.start(); }
    });
    lock.observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  /* ==========================================================
     Globale Layer: Korn + Fortschrittslinie
     ========================================================== */

  function initGlobals() {
    if (!document.querySelector('.ec-grain')) {
      var grain = document.createElement('div');
      grain.className = 'ec-grain';
      grain.setAttribute('aria-hidden', 'true');
      document.body.appendChild(grain);
    }

    var bar = document.querySelector('.ec-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'ec-progress';
      bar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(bar);
    }

    gsap.to(bar, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: { start: 0, end: 'max', scrub: 0.3 }
    });
  }

  /* ==========================================================
     Reveal-Primitive (gelten in jeder Sektion)
     ========================================================== */

  function revealLines(scope) {
    gsap.utils.toArray(scope.querySelectorAll('[data-ec-lines]')).forEach(function (group) {
      var lines = group.querySelectorAll('.ec-line__i');
      if (!lines.length) return;

      /* Ausgangslage in GSAP-Einheiten setzen: eine reine CSS-Prozent-
         Verschiebung liest GSAP als Pixelwert und würde stehen bleiben. */
      gsap.set(lines, { yPercent: 110, y: 0 });

      gsap.to(lines, {
        yPercent: 0,
        duration: reduced ? 0.4 : DUR_LINE,
        ease: reduced ? 'power1.out' : EASE_EXPO,
        stagger: reduced ? 0 : STAGGER_LINE,
        delay: parseFloat(group.getAttribute('data-ec-delay')) || 0,
        scrollTrigger: group.hasAttribute('data-ec-now') ? null : {
          trigger: group, start: START, once: true
        }
      });
    });
  }

  function revealFades(scope) {
    gsap.utils.toArray(scope.querySelectorAll('[data-ec-fade]')).forEach(function (el) {
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: reduced ? 0.4 : 0.9,
        ease: reduced ? 'power1.out' : EASE_EXPO,
        delay: parseFloat(el.getAttribute('data-ec-fade')) || 0,
        scrollTrigger: el.hasAttribute('data-ec-now') ? null : {
          trigger: el, start: START, once: true
        }
      });
    });
  }

  function revealHairs(scope) {
    gsap.utils.toArray(scope.querySelectorAll('[data-ec-hair]')).forEach(function (el) {
      gsap.to(el, {
        scaleX: 1,
        duration: reduced ? 0.4 : DUR_LINE,
        ease: reduced ? 'power1.out' : EASE_EXPO,
        delay: parseFloat(el.getAttribute('data-ec-hair')) || 0,
        scrollTrigger: { trigger: el, start: 'top 92%', once: true }
      });
    });
  }

  function revealHairsVertical(scope) {
    gsap.utils.toArray(scope.querySelectorAll('[data-ec-hair-y]')).forEach(function (el) {
      gsap.to(el, {
        scaleY: 1,
        duration: reduced ? 0.4 : 1.2,
        ease: reduced ? 'power1.out' : EASE_EXPO,
        delay: parseFloat(el.getAttribute('data-ec-hair-y')) || 0,
        scrollTrigger: { trigger: el, start: 'top 88%', once: true }
      });
    });
  }

  function revealImages(scope) {
    gsap.utils.toArray(scope.querySelectorAll('[data-ec-reveal]')).forEach(function (el) {
      var inner = el.querySelector('img, video');
      var tl = gsap.timeline({
        scrollTrigger: { trigger: el, start: START, once: true }
      });

      tl.to(el, {
        clipPath: 'inset(0%)',
        duration: reduced ? 0.4 : DUR_IMG,
        ease: reduced ? 'power1.out' : EASE_EXPO
      });

      if (inner && !reduced) {
        tl.to(inner, { scale: 1, duration: 1.6, ease: EASE_EXPO }, 0);
      } else if (inner) {
        gsap.set(inner, { scale: 1 });
      }
    });
  }

  function initParallax(scope) {
    if (reduced) return;

    gsap.utils.toArray(scope.querySelectorAll('[data-ec-parallax]')).forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-ec-parallax'));
      if (isNaN(speed)) speed = 0.9;
      var shift = (1 - speed) * 100;   /* < 1 = langsamer, > 1 = schneller */

      gsap.fromTo(el,
        { yPercent: shift * 0.5 },
        {
          yPercent: -shift * 0.5,
          ease: 'none',
          scrollTrigger: {
            trigger: el.getAttribute('data-ec-parallax-trigger')
              ? el.closest(el.getAttribute('data-ec-parallax-trigger'))
              : el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: SCRUB
          }
        }
      );
    });
  }

  /* Wort-für-Wort-Aufhellung, an die Scrollposition gebunden. */
  function initFill(scope) {
    gsap.utils.toArray(scope.querySelectorAll('[data-ec-fill]')).forEach(function (el) {
      var words = el.querySelectorAll('.ec-word');
      if (!words.length) return;

      if (reduced) {
        words.forEach(function (w) { w.classList.add('is-lit'); });
        return;
      }

      var stage = el.closest('[data-ec-fill-stage]') || el;

      /* Gehaltene Bühne: die Aufhellung läuft über die ganze Haltestrecke.
         Läuft die Sektion dagegen normal mit (Handy, flache Bildschirme),
         hellt sie sich auf, während sie durchs Bild wandert. Wird bei jeder
         Neuvermessung geprüft, damit Drehen sauber umschaltet. */
      function held() { return stage.offsetHeight > window.innerHeight * 1.15; }

      gsap.to(words, {
        color: function (i, target) {
          return target.classList.contains('ec-word--gold') ? '#C9A96E' : '#F2EFE9';
        },
        ease: 'none',
        duration: 1,
        stagger: 0.55,
        scrollTrigger: {
          trigger: stage,
          start: function () { return held() ? 'top top' : 'top 80%'; },
          end: function () { return held() ? 'bottom bottom' : 'bottom 45%'; },
          scrub: SCRUB,
          invalidateOnRefresh: true
        }
      });
    });
  }

  /* ==========================================================
     Sektions-Module
     ========================================================== */

  var Modules = {};

  /* --- Hero: Auftakt beim Laden, Bild skaliert beim Scrollen --- */
  Modules.hero = function (el) {
    var media = el.querySelector('[data-ec-hero-media]');

    if (media && !reduced) {
      gsap.fromTo(media, { scale: 1, yPercent: 0 }, {
        scale: 1.12,
        yPercent: 6,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: SCRUB }
      });
    }

    var tl = gsap.timeline({ delay: 0.15 });
    var lines = el.querySelectorAll('[data-ec-hero-lines] .ec-line__i');
    var fades = el.querySelectorAll('[data-ec-hero-fade]');

    if (lines.length) {
      gsap.set(lines, { yPercent: 110, y: 0 });
      tl.to(lines, {
        yPercent: 0,
        duration: reduced ? 0.4 : 1.15,
        ease: reduced ? 'power1.out' : EASE_EXPO,
        stagger: reduced ? 0 : STAGGER_LINE
      });
    }

    if (fades.length) {
      tl.to(fades, {
        opacity: 1, y: 0,
        duration: reduced ? 0.4 : 0.9,
        ease: EASE_EXPO,
        stagger: 0.1
      }, '-=0.55');
    }
  };

  /* --- Origin: Bildfolge überblendet, Absätze laufen mit --- */
  Modules.origin = function (el) {
    var frames = el.querySelectorAll('[data-ec-origin-frame]');
    var steps = el.querySelectorAll('[data-ec-origin-step]');
    var stage = el.querySelector('[data-ec-origin-scroll]');
    if (!stage || frames.length < 2) return;

    if (reduced) {
      gsap.set(frames, { opacity: 1 });
      gsap.set(steps, { opacity: 1 });
      return;
    }

    var count = Math.min(frames.length, steps.length || frames.length);

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: stage,
        start: 'top top',
        end: 'bottom bottom',
        scrub: SCRUB
      }
    });

    for (var i = 1; i < count; i++) {
      var at = (i - 0.55) / (count - 0.35);

      tl.to(frames[i], { opacity: 1, duration: 0.35, ease: 'none' }, at);
      tl.to(frames[i - 1], { opacity: 0, duration: 0.35, ease: 'none' }, at);

      var pic = frames[i].querySelector('img');
      if (pic) {
        tl.fromTo(pic, { scale: 1.06 }, { scale: 1, duration: 0.6, ease: 'none' }, at);
      }

      if (steps[i]) {
        tl.to(steps[i - 1], { opacity: 0, y: -14, duration: 0.2, ease: 'none' }, at);
        tl.to(steps[i], { opacity: 1, y: 0, duration: 0.25, ease: 'none' }, at + 0.16);
      }
    }
  };

  /* --- Numbers: Zahlen zählen einmalig hoch --- */
  Modules.numbers = function (el) {
    gsap.utils.toArray(el.querySelectorAll('[data-ec-count]')).forEach(function (node) {
      var target = parseFloat(node.getAttribute('data-ec-count'));
      /* Kleine Zahlen erscheinen hart — ein Hochzählen von 0 auf 1 wirkt albern. */
      if (isNaN(target) || target < 10) return;

      if (reduced) return;

      /* Bis die Zahl ins Bild kommt, bleibt der echte Wert im HTML stehen
         (Suchmaschinen, KI-Crawler, Screenshots). Erst beim Eintritt am unteren
         Rand springt sie auf 0 und zählt hoch — vorher stand dort sofort „0 MIN". */
      var obj = { v: 0 };
      ScrollTrigger.create({
        trigger: node,
        start: 'top bottom',
        once: true,
        onEnter: function () {
          node.textContent = '0';
          gsap.to(obj, {
            v: target,
            duration: 1.1,
            ease: 'power2.out',
            onUpdate: function () { node.textContent = Math.round(obj.v); },
            onComplete: function () { node.textContent = String(target); }
          });
        }
      });
    });
  };

  /* --- Collection: gepinnte Horizontal-Sequenz (Desktop) bzw. Swipe --- */
  Modules.collection = function (el) {
    var scroll = el.querySelector('[data-ec-coll-scroll]');
    var track = el.querySelector('[data-ec-coll-track]');
    var bar = el.querySelector('[data-ec-coll-bar]');
    var counter = el.querySelector('[data-ec-coll-counter]');
    var panels = el.querySelectorAll('[data-ec-coll-panel]');
    if (!scroll || !track) return;

    function setCounter(i) {
      if (counter && panels.length) {
        counter.textContent = ('0' + Math.min(Math.max(i, 1), panels.length)).slice(-2);
      }
    }

    /* matchMedia statt einmaliger Breitenprüfung: beim Wechsel zwischen
       Desktop und Mobil (Drehen, Fenstergröße) baut GSAP sauber um. */
    var mm = gsap.matchMedia();

    mm.add('(min-width: 901px) and (prefers-reduced-motion: no-preference)', function () {
      el.classList.remove('is-swipe');

      var distance = 0;

      function measure() {
        distance = Math.max(0, track.scrollWidth - window.innerWidth);
        scroll.style.height = (window.innerHeight + distance) + 'px';
      }

      measure();
      ScrollTrigger.addEventListener('refreshInit', measure);

      gsap.to(track, {
        x: function () { return -distance; },
        ease: 'none',
        scrollTrigger: {
          trigger: scroll,
          start: 'top top',
          end: 'bottom bottom',
          scrub: SCRUB,
          invalidateOnRefresh: true,
          onUpdate: function (self) {
            if (bar) gsap.set(bar, { scaleX: self.progress });
            setCounter(Math.floor(self.progress * panels.length) + 1);
          }
        }
      });

      /* Leichter Innen-Parallax der Panelbilder */
      gsap.utils.toArray(el.querySelectorAll('.ec-coll__img')).forEach(function (img) {
        gsap.fromTo(img, { xPercent: -6 }, {
          xPercent: 6,
          ease: 'none',
          scrollTrigger: {
            trigger: scroll, start: 'top top', end: 'bottom bottom', scrub: SCRUB
          }
        });
      });

      return function () {
        ScrollTrigger.removeEventListener('refreshInit', measure);
        scroll.style.height = '';
        gsap.set(track, { x: 0 });
      };
    });

    mm.add('(max-width: 900px), (prefers-reduced-motion: reduce)', function () {
      el.classList.add('is-swipe');
      scroll.style.height = '';

      function onScroll() {
        var per = track.scrollWidth / panels.length;
        setCounter(Math.round(track.scrollLeft / per) + 1);
        if (bar) {
          var max = track.scrollWidth - track.clientWidth;
          gsap.set(bar, { scaleX: max > 0 ? track.scrollLeft / max : 0 });
        }
      }

      track.addEventListener('scroll', onScroll, { passive: true });
      return function () { track.removeEventListener('scroll', onScroll); };
    });
  };

  /* --- Trade: Magnetic Button (nur feine Zeiger) --- */
  Modules.trade = function (el) {
    if (reduced || !window.matchMedia('(pointer: fine)').matches) return;

    gsap.utils.toArray(el.querySelectorAll('[data-ec-magnetic]')).forEach(function (btn) {
      var setX = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3.out' });
      var setY = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3.out' });

      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        setX(gsap.utils.clamp(-8, 8, (e.clientX - (r.left + r.width / 2)) * 0.25));
        setY(gsap.utils.clamp(-8, 8, (e.clientY - (r.top + r.height / 2)) * 0.25));
      });

      btn.addEventListener('mouseleave', function () { setX(0); setY(0); });
    });
  };

  /* --- Showcase: Störbild bleibt stehen, Weiss steigt darüber --- */
  Modules.showcase = function (el) {
    if (reduced) return;
    var img = el.querySelector('[data-ec-showcase-img]');
    if (!img) return;

    gsap.fromTo(img, { scale: 1.1 }, {
      scale: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: el.querySelector('[data-ec-showcase-stage]') || el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: SCRUB
      }
    });
  };

  /* --- Film: die Scrollposition steuert die Abspielstelle des Videos --- */
  Modules.film = function (el) {
    var video = el.querySelector('[data-ec-film-video]');
    var scroll = el.querySelector('[data-ec-film-scroll]');
    if (!video || !scroll) return;

    if (el.hasAttribute('data-ec-film-mobile-off') && window.matchMedia('(max-width: 900px)').matches) {
      video.parentNode.removeChild(video);
      return;
    }

    var mm = gsap.matchMedia();

    /* Desktop: scrubben. */
    mm.add('(min-width: 901px) and (prefers-reduced-motion: no-preference)', function () {
      video.loop = false;
      video.pause();

      /* Safari/iOS dekodiert erst nach einem play(). Einmal antippen, sofort stoppen. */
      function prime() {
        var p = video.play();
        if (p && p.then) {
          p.then(function () { video.pause(); video.currentTime = 0; }).catch(function () {});
        } else {
          video.pause();
        }
      }

      if (video.readyState >= 2) { prime(); }
      else { video.addEventListener('loadeddata', prime, { once: true }); }

      var head = { t: 0 };

      var tween = gsap.to(head, {
        t: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: scroll,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.6
        },
        onUpdate: function () {
          var d = video.duration;
          if (d && isFinite(d)) {
            video.currentTime = Math.max(0, Math.min(d - 0.03, head.t * d));
          }
        }
      });

      return function () { tween.kill(); };
    });

    /* Mobil und bei reduzierter Bewegung: ruhige Schleife statt Scrub. */
    mm.add('(max-width: 900px), (prefers-reduced-motion: reduce)', function () {
      video.loop = true;
      video.muted = true;
      var p = video.play();
      if (p && p.catch) { p.catch(function () {}); }
      return function () { video.pause(); };
    });
  };

  /* --- Leo: Bilderkarussell über natives Scroll-Snapping --- */
  Modules.founder = function (el) {
    var track = el.querySelector('[data-ec-fo-track]');
    if (!track) return;

    var slides = el.querySelectorAll('[data-ec-fo-slide]');
    var prev = el.querySelector('[data-ec-fo-prev]');
    var next = el.querySelector('[data-ec-fo-next]');
    var counter = el.querySelector('[data-ec-fo-counter]');
    var bar = el.querySelector('[data-ec-fo-bar]');
    if (slides.length < 2) return;

    function maxScroll() { return track.scrollWidth - track.clientWidth; }

    function index() {
      var step = track.scrollWidth / slides.length;
      return Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
    }

    function paint() {
      var i = index();
      var max = maxScroll();

      if (counter) counter.textContent = ('0' + (i + 1)).slice(-2);
      if (bar) gsap.set(bar, { scaleX: max > 0 ? track.scrollLeft / max : 0 });

      /* Ein Pfeil ins Leere ist irreführend — am Rand abschalten. */
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    }

    function go(dir) {
      var step = track.scrollWidth / slides.length;
      track.scrollTo({
        left: track.scrollLeft + dir * step,
        behavior: reduced ? 'auto' : 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', paint, { passive: true });

    paint();
  };

  Modules.journal = function () { /* nutzt ausschliesslich Standard-Reveals */ };
  Modules.statement = function () { /* nutzt ausschliesslich data-ec-fill */ };
  Modules.philosophy = function () { /* nutzt data-ec-fill + Standard-Reveals */ };
  Modules.gallery = function () { /* nutzt data-ec-parallax + Standard-Reveals */ };

  /* --- Wunsch: drei Linienzeichnungen zeichnen sich nacheinander selbst --- */
  Modules.wish = function (el) {
    var deadline = el.querySelector('[data-ec-wx-deadline]');
    if (deadline) wishDeadline(deadline);

    /* Ohne Bewegung stehen die Zeichnungen fertig da */
    if (reduced) return;

    var steps = el.querySelectorAll('[data-ec-wx-step]');
    /* Nebeneinander (Desktop) läuft die Geschichte von links nach rechts */
    var row = window.matchMedia('(min-width: 901px)').matches;

    gsap.utils.toArray(steps).forEach(function (step, i) {
      var lines = gsap.utils.toArray(step.querySelectorAll('[data-draw] > *')).filter(function (node) {
        if (typeof node.getTotalLength !== 'function') return false;
        var len = node.getTotalLength();
        if (!len) return false;
        node.style.strokeDasharray = len;
        node.style.strokeDashoffset = len;
        return true;
      });
      var extras = step.querySelectorAll('.ec-wx__pearls circle, .ec-wx__tap, [data-ec-wx-route], [data-ec-wx-letter]');
      gsap.set(extras, { opacity: 0 });

      var tl = gsap.timeline({
        paused: true,
        onStart: function () { step.classList.add('is-active'); },
        onComplete: function () { step.classList.add('is-drawn'); flyLetter(step); }
      });
      tl.to(lines, { strokeDashoffset: 0, duration: 1.2, ease: 'power2.inOut', stagger: 0.09 });
      tl.to(extras, { opacity: 1, duration: 0.5, ease: 'power1.out', stagger: 0.03 }, '-=0.5');

      ScrollTrigger.create({
        trigger: step,
        start: 'top 82%',
        once: true,
        onEnter: function () { gsap.delayedCall(row ? i * 0.55 : 0, function () { tl.play(); }); }
      });
    });
  };

  /* Schritt 2: der Brief fliegt immer wieder von Handy zu Handy — nur solange sichtbar */
  function flyLetter(step) {
    var route = step.querySelector('[data-ec-wx-route]');
    var letter = step.querySelector('[data-ec-wx-letter]');
    if (!route || !letter || typeof route.getPointAtLength !== 'function') return;

    var length = route.getTotalLength();
    var pos = { t: 0 };
    var loop = gsap.to(pos, {
      t: 1,
      duration: 2.4,
      ease: 'power1.inOut',
      repeat: -1,
      repeatDelay: 0.9,
      onUpdate: function () {
        var p = route.getPointAtLength(pos.t * length);
        letter.setAttribute('transform', 'translate(' + p.x.toFixed(1) + ' ' + p.y.toFixed(1) + ')');
      }
    });

    ScrollTrigger.create({
      trigger: step,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: function (self) { if (self.isActive) { loop.play(); } else { loop.pause(); } }
    });
  }

  /* Wunsch-Sektion · Bestellschluss (gleiche Logik wie assets/ec-wish.js)
     Jetzt in Wiener Zeit als „JJJJ-MM-TT HH:MM" — direkt vergleichbar */
  function nowVienna() {
    try {
      var p = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).forEach(function (part) { p[part.type] = part.value; });
      return p.year + '-' + p.month + '-' + p.day + ' ' + p.hour + ':' + p.minute;
    } catch (e) {
      return new Date().toISOString().slice(0, 16).replace('T', ' ');
    }
  }

  /* Bestellschluss „JJJJ-MM-TT" oder „JJJJ-MM-TT HH:MM" (Wiener Zeit) als Text in der
     Seitensprache, z. B. „22. Dezember um 10:00". Danach leer — der Hinweis verschwindet. */
  function deadlineLabel(value, lang) {
    var m = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    if (!m) return '';
    var hasTime = m[4] != null;
    var hh = hasTime ? ('0' + m[4]).slice(-2) : '23';
    var mm = hasTime ? m[5] : '59';
    if (nowVienna() >= m[1] + '-' + m[2] + '-' + m[3] + ' ' + hh + ':' + mm) return '';

    var opts = { day: 'numeric', month: 'long' };
    if (hasTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
    try {
      return new Intl.DateTimeFormat(lang || undefined, opts)
        .format(new Date(+m[1], +m[2] - 1, +m[3], +hh, +mm));
    } catch (e) {
      return m[3] + '.' + m[2] + '.' + (hasTime ? ' ' + hh + ':' + mm : '');
    }
  }

  function wishDeadline(node) {
    var label = deadlineLabel(node.getAttribute('data-date'), root.lang);
    if (!label) return;

    var tpl = node.getAttribute('data-text') || node.getAttribute('data-default') || '';
    node.textContent = tpl.split('{date}').join(label).split('%DATE%').join(label);
    node.hidden = false;
  }

  /* ==========================================================
     Bootstrap
     ========================================================== */

  function initScope(scope) {
    revealLines(scope);
    revealFades(scope);
    revealHairs(scope);
    revealHairsVertical(scope);
    revealImages(scope);
    initParallax(scope);
    initFill(scope);

    gsap.utils.toArray(scope.querySelectorAll('[data-ec-section]')).forEach(function (el) {
      var name = el.getAttribute('data-ec-section');
      if (Modules[name] && !el.hasAttribute('data-ec-ready')) {
        el.setAttribute('data-ec-ready', '');
        Modules[name](el);
      }
    });

    if (scope.hasAttribute && scope.hasAttribute('data-ec-section')) {
      var own = scope.getAttribute('data-ec-section');
      if (Modules[own] && !scope.hasAttribute('data-ec-ready')) {
        scope.setAttribute('data-ec-ready', '');
        Modules[own](scope);
      }
    }
  }

  function boot() {
    initLenis();
    initGlobals();
    initScope(document);

    root.setAttribute('data-ec-motion', 'ready');

    /* Layout nach dem Laden der Bilder neu vermessen */
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });

    var resizeTimer;
    var lastWidth = window.innerWidth;

    window.addEventListener('resize', function () {
      /* Auf Touch nur bei echter Breitenänderung (Drehen) neu vermessen.
         Reine Höhenänderungen kommen von der Adressleiste des Browsers. */
      if (touch && window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { ScrollTrigger.refresh(); }, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme-Editor: Sektion neu geladen / entfernt */
  document.addEventListener('shopify:section:load', function (e) {
    gsap.set(e.target.querySelectorAll('.ec-line__i'), { yPercent: 110 });
    initScope(e.target);
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function () {
    ScrollTrigger.refresh();
  });
})();
